"use client";
import React from "react";
import Icon from "@/components/primitives/icon";
import {
  DepthCharts,
  Sport,
  StartingLineup as StartingLineupInfo,
  TeamBasic,
  TeamColor,
} from "common";
import NotFound from "../primitives/not-found";
import { LoadingCircle } from "../primitives/loading";
import useFetch from "@/utils/fetch";
import TeamDisplay from "../team/display";
import Card from "../primitives/card";
import { createPlayerProfileSlug } from "@/utils/url";
import TeamLogo from "../team/logo";
import Link from "next/link";
import { truncateName } from "@/utils/injuries";

const cols = ["Player", "Position"];

export default function StartingLineup({
  awayTeam,
  homeTeam,
  sport,
  gameId,
}: {
  awayTeam: TeamBasic & TeamColor;
  homeTeam: TeamBasic & TeamColor;
  sport: Sport;
  gameId: number;
}) {
  // const request: string = `${process.env.NEXT_PUBLIC_API_URL}/api/sports/${sport}/starting-lineups/${gameId}`;
  const request: string = `${process.env.NEXT_PUBLIC_API_URL}/${sport}/StartingLineup/${gameId}`;
  const { data, isLoading, isError, error } = useFetch<{
    away: DepthCharts[];
    home: DepthCharts[];
  } | null>(null, request, []);
  const length = data?.home.length || 0;
  // console.log("data from lineup", data);
  return (
    <Card
      header={{
        title: (
          <>
            <Icon name="team" size="md" gradient="flame" idKey={2} />
            <span>{sport === "nfl" ? "Key Players" : "Starting Lineup"}</span>
          </>
        ),
      }}
    >
      <div className="table-container mt-2">
        {isLoading ? (
          <div className="flex justify-center items-center h-[220px]">
            <LoadingCircle />
          </div>
        ) : !isLoading && (!data || isError) ? (
          <NotFound label="Lineup TBD" />
        ) : (
          data && (
            <div className="table-container mt-2 text-sm-medium text-secondary-300">
              <div className="flex flex-row justify-between">
                <Link
                  href={createPlayerProfileSlug(sport, awayTeam.id)}
                  className="flex-basis-6/12 flex-row flex items-center gap-4 uppercase text-left"
                >
                  <div
                    className="w-12 h-12 border rounded-full flex items-center justify-center"
                    style={{ borderColor: `#${awayTeam.primaryColor}` }}
                  >
                    <TeamLogo team={awayTeam} size="sm" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="text-title-bold">{awayTeam.name}</div>
                    <div className="text-label-lg text-secondary-400">
                      {awayTeam.abbreviation}
                    </div>
                  </div>
                </Link>
                <Link
                  href={createPlayerProfileSlug(sport, homeTeam.id)}
                  className="flex-basis-6/12 flex-row flex items-center gap-4 uppercase text-right"
                >
                  <div
                    className="w-12 h-12 border rounded-full flex items-center justify-center order-2"
                    style={{ borderColor: `#${homeTeam.primaryColor}` }}
                  >
                    <TeamLogo team={homeTeam} size="sm" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="text-title-bold">{homeTeam.name}</div>
                    <div className="text-label-lg text-secondary-400">
                      {homeTeam.abbreviation}
                    </div>
                  </div>
                </Link>
              </div>
              {Array.from({ length }).map((_, index) => (
                <div
                  key={index}
                  className={`flex flex-row justify-between px-3 h-[46px] items-center rounded-md ${
                    index % 2 === 1 ? "bg-surface-300" : ""
                  }`}
                >
                  {data.away[index] ? (
                    <Link
                      href={createPlayerProfileSlug(
                        sport,
                        data.away[index]?.id
                      )}
                      className="flex flex-row gap-1 items-center basis-5/12 h-[46px]"
                    >
                      {truncateName(data.away[index].firstName)}{" "}
                      {data.away[index].lastName}
                      <span className="uppercase text-secondary-400">
                        #{data.away[index].number}
                      </span>
                    </Link>
                  ) : (
                    <div className="flex flex-row gap-1 items-center basis-5/12 h-[46px]">N/A</div>
                  )}

                  {data.home[index] && (
                    <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
                      {data.home[index].position}
                    </div>
                  )}

                  {data.home[index] ? (
                    <Link
                      href={createPlayerProfileSlug(
                        sport,
                        data.home[index]?.id
                      )}
                      className="flex flex-row gap-1 items-center basis-5/12 justify-end h-[46px]"
                    >
                      <span className="uppercase text-secondary-400">
                        #{data.home[index].number}
                      </span>
                      {truncateName(data.home[index].firstName)}{" "}
                      {data.home[index].lastName}
                    </Link>
                  ) : (
                    <div className="flex flex-row gap-1 items-center basis-5/12 h-[46px]">N/A</div>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </Card>
  );
}

export function StartingLineupTable({
  data,
  sport,
}: {
  data: StartingLineupInfo;
  sport: Sport;
}) {
  const length = data?.home.length || 0;

  return (
    <div className="bg-surface-700 p-2 rounded-br-md rounded-bl-md">
      {Array.from({ length }).map((_, index) => (
        <div
          key={index}
          className={`flex flex-row justify-between px-3 h-[46px] items-center rounded-md ${
            index % 2 === 1 ? "bg-surface-300" : ""
          }`}
        >
          <Link
            href={createPlayerProfileSlug(sport, data.away[index].id)}
            className="flex flex-row gap-1 items-center basis-5/12 h-[46px] text-sm-medium whitespace-nowrap"
          >
            {truncateName(data.away[index].firstName)}{" "}
            {data.away[index].lastName}
            <span className="uppercase text-secondary-400 text-label-lg">
              #{data.away[index].number}
            </span>
          </Link>
          <div className="text-label-lg text-secondary-400 justify-center flex basis-2/12">
            {data.home[index].position}
          </div>
          <Link
            href={createPlayerProfileSlug(sport, data.home[index].id)}
            className="flex flex-row gap-1 items-center basis-5/12 justify-end h-[46px] text-sm-medium"
          >
            <span className="uppercase text-secondary-400 text-label-lg">
              #{data.home[index].number}
            </span>
            {truncateName(data.home[index].firstName)}{" "}
            {data.home[index].lastName}
          </Link>
        </div>
      ))}
    </div>
  );
}

// function StartingLineupsTable({
//   data,
//   showSport,
//   showTeam,
// }: {
//   data: DepthCharts[];
//   showSport?: boolean;
//   showTeam?: boolean;
// }) {
//   return (
//     <table className="w-full text-left banded">
//       <thead>
//         <tr className="text-label-sm-medium text-secondary-500 uppercase">
//           {cols.map((col, i) => (
//             <th
//               key={i}
//               style={{ width: `${col === "Player" ? "300px" : "80px"}` }}
//             >
//               {col === "Status" ? (
//                 <div className="flex justify-end">{col}</div>
//               ) : (
//                 col
//               )}
//             </th>
//           ))}
//         </tr>
//       </thead>
//       <tbody>
//         {data?.map((player, i) => (
//           <tr key={i}>
//             <td>
//               <div className="flex flex-col gap-1">
//                 <div className="flex gap-1 py-1">{player.name}</div>
//               </div>
//             </td>
//             <td>{player.position}</td>
//           </tr>
//         ))}
//       </tbody>
//     </table>
//   );
// }
